// ════════════════════════════════════════════════════════════════════
// daily-tips.ts — bite-sized "Wissenswertes" and "Tipp" snippets for
// the daily briefing card on /today.
//
// Each tip is:
//   - Age-banded (minWeeks/maxWeeks)
//   - One sentence title + 2–3 sentence body
//   - EN + DE
//   - Evidence-grounded (AAP/DGKJ/WHO/CDC)
//
// Daily rotation: tipOfTheDayFor(ageWeeks, babyId, date) picks one
// deterministically. Same baby + same day = same tip. Next day rotates.
// ════════════════════════════════════════════════════════════════════

import { isGerman } from '../i18n/locale.js';

export type TipKind = 'tipp' | 'wissenswertes';

export interface DailyTip {
  id: string;
  ageWeeksMin: number;
  ageWeeksMax: number;
  kind: TipKind;
  title: string;
  title_de: string;
  body: string;
  body_de: string;
}

export interface LocalizedTip {
  id: string;
  kind: TipKind;
  kindLabel: string;
  title: string;
  body: string;
}

export const DAILY_TIPS: DailyTip[] = [
  // ──────────────────────────────────────────────────────────────
  // Newborn 0–12 weeks
  // ──────────────────────────────────────────────────────────────
  {
    id: 'newborn-cluster-feeding',
    ageWeeksMin: 0,
    ageWeeksMax: 12,
    kind: 'wissenswertes',
    title: 'Cluster feeding looks scary but is normal',
    title_de: 'Clusterfeeding sieht beängstigend aus — ist aber normal',
    body: 'In the evenings, especially in the first weeks, babies feed almost continuously for 3–4 hours. This is not low milk supply. It is your baby loading up before the longer night stretch.',
    body_de: 'Abends, besonders in den ersten Wochen, trinken Babys oft fast durchgehend 3–4 Stunden lang. Das ist kein Zeichen für zu wenig Milch — sondern dein Baby tankt für die lange Nacht-Phase auf.',
  },
  {
    id: 'newborn-witching-hour',
    ageWeeksMin: 2,
    ageWeeksMax: 12,
    kind: 'wissenswertes',
    title: 'The "witching hour" is a thing',
    title_de: 'Die „Hexenstunde" ist real',
    body: 'Many newborns get inconsolably fussy between 5 and 11 pm. It peaks around 6–8 weeks and fades by 12. It is not colic in most cases — it is an immature nervous system processing the day.',
    body_de: 'Viele Neugeborene werden zwischen 17 und 23 Uhr untröstlich. Es gipfelt um 6–8 Wochen und legt sich bis Woche 12. Meist sind das keine Koliken — sondern ein unreifes Nervensystem, das den Tag verarbeitet.',
  },
  {
    id: 'newborn-poop-color',
    ageWeeksMin: 0,
    ageWeeksMax: 8,
    kind: 'wissenswertes',
    title: 'Yellow seedy poop is the goal',
    title_de: 'Gelber, körniger Stuhl ist das Ziel',
    body: 'For breastfed newborns, mustard-yellow with little seed-like bits is healthy. Formula-fed babies have firmer, tan-colored stools. Both normal. Worrying colors: white, black after day 4, or red streaks.',
    body_de: 'Bei gestillten Neugeborenen ist senfgelber, körniger Stuhl gesund. Bei Flaschen-gefütterten ist er fester und bräunlich. Beides normal. Warnzeichen-Farben: weiss, schwarz nach Tag 4, oder rote Streifen.',
  },
  {
    id: 'newborn-startle-reflex',
    ageWeeksMin: 0,
    ageWeeksMax: 16,
    kind: 'wissenswertes',
    title: 'The startle reflex is wired in',
    title_de: 'Der Moro-Reflex ist eingebaut',
    body: 'When your baby jumps and throws arms out, that is the Moro reflex — a built-in survival response. It fades around month 4–6. Swaddling helps in the first weeks because it dampens the reflex.',
    body_de: 'Wenn dein Baby plötzlich zuckt und die Arme rauswirft, ist das der Moro-Reflex — eine eingebaute Schutzreaktion. Er verschwindet um Monat 4–6. Pucken hilft in den ersten Wochen, weil es den Reflex dämpft.',
  },
  {
    id: 'newborn-tummy-time',
    ageWeeksMin: 0,
    ageWeeksMax: 12,
    kind: 'tipp',
    title: 'Tummy time can start on day one',
    title_de: 'Bauchlage kann ab Tag 1 starten',
    body: 'Lay your newborn on your chest while you are reclined. That counts as tummy time and is much easier to tolerate than the floor. Aim for short, frequent rounds — 1–2 minutes is plenty at first.',
    body_de: 'Leg dein Neugeborenes auf deine Brust, während du halb-liegst. Das zählt als Bauchlage und ist viel angenehmer als auf dem Boden. Ziel: kurz und häufig — 1–2 Minuten reichen am Anfang völlig.',
  },
  {
    id: 'newborn-sleep-cycles',
    ageWeeksMin: 0,
    ageWeeksMax: 16,
    kind: 'wissenswertes',
    title: 'Newborn sleep cycles are 45 minutes',
    title_de: 'Neugeborenen-Schlafzyklen dauern 45 Minuten',
    body: "An adult sleep cycle is 90 minutes. A newborn's is half that — which is why they often wake up after 30–45 minutes of napping. Not a problem to solve; just the architecture of their sleep at this age.",
    body_de: 'Ein Erwachsenen-Schlafzyklus dauert 90 Minuten. Bei Neugeborenen nur die Hälfte — deshalb wachen sie oft nach 30–45 Minuten Schlaf auf. Kein Problem zum Lösen, einfach die Architektur ihres Schlafs in diesem Alter.',
  },
  {
    id: 'newborn-skin-to-skin',
    ageWeeksMin: 0,
    ageWeeksMax: 12,
    kind: 'tipp',
    title: 'Skin-to-skin is medicine',
    title_de: 'Haut-an-Haut ist Medizin',
    body: "Studies show skin-to-skin (kangaroo care) lowers your baby's heart rate, stabilizes breathing, regulates temperature, and boosts your milk supply. 20–30 minutes a day, ideally with the parent shirtless.",
    body_de: 'Studien zeigen: Haut-an-Haut (Känguru-Methode) senkt die Herzfrequenz des Babys, stabilisiert die Atmung, reguliert die Temperatur und unterstützt deine Milchmenge. 20–30 Minuten täglich, idealerweise mit nacktem Oberkörper.',
  },
  {
    id: 'newborn-spit-up-vs-vomit',
    ageWeeksMin: 0,
    ageWeeksMax: 16,
    kind: 'wissenswertes',
    title: 'Spit-up vs vomit: huge difference',
    title_de: 'Spucken vs. Erbrechen: grosser Unterschied',
    body: 'Spit-up dribbles out passively, baby is fine. Projectile vomit shoots out forcefully — and if it happens repeatedly, call your pediatrician. The difference is one calm gulp vs an angry geyser.',
    body_de: 'Spucken läuft passiv heraus, das Baby ist entspannt. Schwallartiges Erbrechen schiesst mit Druck heraus — wenn das wiederholt passiert, in der Praxis anrufen. Unterschied: ruhiger Schluck vs. zorniger Geysir.',
  },

  // ──────────────────────────────────────────────────────────────
  // 3–6 months
  // ──────────────────────────────────────────────────────────────
  {
    id: 'sleep-regression-4mo',
    ageWeeksMin: 14,
    ageWeeksMax: 22,
    kind: 'wissenswertes',
    title: 'The 4-month "regression" is actually progression',
    title_de: 'Die 4-Monats-„Regression" ist eigentlich Fortschritt',
    body: 'Around 14–18 weeks, sleep gets dramatically worse for 1–4 weeks. Strictly, this is not regression — sleep cycles mature into adult-like patterns. Big change, but it passes.',
    body_de: 'Zwischen Woche 14 und 18 wird der Schlaf für 1–4 Wochen dramatisch schlechter. Streng genommen keine Regression — die Schlafzyklen reifen erwachsen-ähnlich. Grosse Sache, die aber vorbeigeht.',
  },
  {
    id: 'rolling-safety',
    ageWeeksMin: 12,
    ageWeeksMax: 24,
    kind: 'tipp',
    title: 'Once rolling starts: the swaddle goes',
    title_de: 'Sobald sich dein Baby dreht: der Pucksack muss weg',
    body: 'The moment your baby rolls — in either direction — stop swaddling. Arms must be free in case they roll onto their tummy in sleep. Use a sleep sack instead; same warmth, no restraint.',
    body_de: 'Sobald sich dein Baby dreht — egal in welche Richtung — keinen Pucksack mehr. Die Arme müssen frei sein, falls sie sich im Schlaf auf den Bauch drehen. Stattdessen Schlafsack: gleiche Wärme, ohne Einengung.',
  },
  {
    id: 'tongue-thrust',
    ageWeeksMin: 16,
    ageWeeksMax: 24,
    kind: 'wissenswertes',
    title: 'The tongue-thrust reflex is a built-in safety mechanism',
    title_de: 'Der Zungenstossreflex ist ein eingebauter Schutzmechanismus',
    body: 'If your baby pushes everything out of their mouth with their tongue, they are not "rejecting" solids — the reflex is just still active. It usually fades around month 5–6, one sign of readiness for solids.',
    body_de: 'Wenn dein Baby alles mit der Zunge wieder rausschiebt, lehnt es Beikost nicht „ab" — der Reflex ist noch aktiv. Er verschwindet meist um Monat 5–6, eines der Zeichen für Beikostreife.',
  },
  {
    id: 'distracted-feeding',
    ageWeeksMin: 14,
    ageWeeksMax: 28,
    kind: 'wissenswertes',
    title: 'Distracted feeders are not on strike',
    title_de: 'Abgelenkte Trinker sind nicht im Stillstreik',
    body: 'Around 4–5 months, babies discover the world. They pop off mid-feed to look around. This is curiosity, not nursing strike. A quieter room helps; otherwise, embrace the chaos.',
    body_de: 'Mit etwa 4–5 Monaten entdecken Babys die Welt. Sie lassen mitten in der Mahlzeit los, um sich umzuschauen. Das ist Neugier, kein Stillstreik. Ein ruhiger Raum hilft; sonst: das Chaos akzeptieren.',
  },
  {
    id: 'no-water-yet',
    ageWeeksMin: 12,
    ageWeeksMax: 26,
    kind: 'wissenswertes',
    title: 'Babies do not need water before 6 months',
    title_de: 'Babys brauchen kein Wasser vor 6 Monaten',
    body: 'Breast milk and formula provide all the hydration. Giving water to a baby under 6 months can dilute electrolytes and interfere with milk intake. After 6 months, sips with meals are fine.',
    body_de: 'Muttermilch und Säuglingsmilch decken den gesamten Flüssigkeitsbedarf. Wasser bei Babys unter 6 Monaten kann den Elektrolythaushalt stören und die Milchaufnahme verringern. Nach 6 Monaten sind kleine Schlucke zur Mahlzeit okay.',
  },
  {
    id: 'sleep-by-night',
    ageWeeksMin: 16,
    ageWeeksMax: 26,
    kind: 'wissenswertes',
    title: '"Sleeping through" means 5 hours, not 8',
    title_de: '„Durchschlafen" heisst 5 Stunden, nicht 8',
    body: "Pediatric definition of 'sleeping through the night' is a 5-hour stretch — usually 11 pm to 4 am. Most babies hit this between 3 and 6 months. Anyone who tells you their 4-month-old sleeps 12 hours is either lying or extremely lucky.",
    body_de: 'Pädiatrisch heisst „Durchschlafen" eine 5-Stunden-Phase — meist von 23 bis 4 Uhr. Die meisten Babys schaffen das zwischen 3 und 6 Monaten. Wer behauptet, das 4-Monats-Baby schlafe 12 Stunden, lügt oder hat extrem viel Glück.',
  },

  // ──────────────────────────────────────────────────────────────
  // 6–8 months
  // ──────────────────────────────────────────────────────────────
  {
    id: 'iron-priority',
    ageWeeksMin: 24,
    ageWeeksMax: 36,
    kind: 'wissenswertes',
    title: 'Iron is the #1 nutrient when solids start',
    title_de: 'Eisen ist der wichtigste Nährstoff beim Beikoststart',
    body: "Iron stores from pregnancy run out around 6 months — and breast milk alone does not refill them. Red meat, lentils, iron-fortified cereal at most meals. This is why 'iron-first' beats 'vegetables-first' for the very early weeks.",
    body_de: 'Die Eisenspeicher aus der Schwangerschaft sind um Monat 6 erschöpft — und Muttermilch füllt sie nicht auf. Rotes Fleisch, Linsen, eisen-angereicherter Brei bei den meisten Mahlzeiten. Deshalb „Eisen zuerst" statt „Gemüse zuerst" in den ersten Beikostwochen.',
  },
  {
    id: 'gag-vs-choke',
    ageWeeksMin: 24,
    ageWeeksMax: 60,
    kind: 'tipp',
    title: 'Gagging is loud. Choking is silent.',
    title_de: 'Würgen ist laut. Verschlucken ist still.',
    body: "Gagging is protective — food gets pushed back forward. It is scary to watch but the baby is safe. Real choking is silent, baby cannot breathe or cry. Learn the difference; it will save you a lot of panic.",
    body_de: 'Würgen ist ein Schutzreflex — das Essen wird wieder nach vorne befördert. Sieht beängstigend aus, aber das Baby ist sicher. Echtes Verschlucken ist still — das Baby kann nicht atmen oder schreien. Den Unterschied zu kennen erspart viel Panik.',
  },
  {
    id: 'leap-allergens',
    ageWeeksMin: 24,
    ageWeeksMax: 36,
    kind: 'wissenswertes',
    title: 'Allergens: early and often beats wait-and-see',
    title_de: 'Allergene: früh und häufig schlägt abwarten',
    body: 'The LEAP study (2015) flipped 20 years of advice. Introducing peanut, egg, and other top allergens between 4–11 months actively reduces allergy risk. Smooth peanut butter thinned with banana is the safe-texture entry point.',
    body_de: 'Die LEAP-Studie (2015) hat 20 Jahre Empfehlungen umgedreht. Frühe Einführung von Erdnuss, Ei und anderen Top-Allergenen zwischen 4–11 Monaten senkt das Allergierisiko aktiv. Glattes Erdnussmus mit Banane verdünnt ist der sichere Texturen-Einstieg.',
  },
  {
    id: 'no-honey',
    ageWeeksMin: 24,
    ageWeeksMax: 52,
    kind: 'tipp',
    title: 'No honey until the first birthday',
    title_de: 'Kein Honig bis zum 1. Geburtstag',
    body: 'Honey can contain Clostridium botulinum spores. An adult gut handles them. An infant gut cannot — botulism is rare but serious. Watch for honey in unexpected places: cereals, baby pouches with sweet flavors.',
    body_de: 'Honig kann Clostridium-botulinum-Sporen enthalten. Ein Erwachsenen-Darm verarbeitet sie problemlos. Ein Baby-Darm nicht — Botulismus ist selten aber gefährlich. Achte auf Honig in unerwarteten Stellen: Cerealien, süsse Quetschies.',
  },
  {
    id: 'food-before-one',
    ageWeeksMin: 24,
    ageWeeksMax: 52,
    kind: 'wissenswertes',
    title: '"Food before one is just for fun" — only partly true',
    title_de: '„Essen vor 1 ist nur Spass" — nur teilweise wahr',
    body: 'Milk remains the main calorie source until 12 months. But solids are NOT just play — iron, zinc, allergen exposure, and texture skills all build now. The phrase undersells how important first foods are.',
    body_de: 'Milch bleibt bis 12 Monate die Hauptkalorienquelle. Aber Beikost ist NICHT nur Spielerei — Eisen, Zink, Allergen-Exposition und Texturen-Skills bauen sich jetzt auf. Der Spruch unterschätzt, wie wichtig die ersten Lebensmittel sind.',
  },
  {
    id: 'puree-vs-blw',
    ageWeeksMin: 24,
    ageWeeksMax: 40,
    kind: 'wissenswertes',
    title: 'Puree vs Baby-Led Weaning: both work',
    title_de: 'Brei vs. Baby-led Weaning: beides funktioniert',
    body: 'Studies show no long-term difference between purees and BLW for nutrition or eating skills. The honest truth: most families end up with a mix. Pick what fits your kitchen and your nerves.',
    body_de: 'Studien zeigen keinen langfristigen Unterschied zwischen Brei und BLW bei Ernährung oder Essfähigkeit. Die ehrliche Wahrheit: die meisten Familien landen bei einer Mischung. Wähle was zu deiner Küche und deinen Nerven passt.',
  },

  // ──────────────────────────────────────────────────────────────
  // 8–12 months
  // ──────────────────────────────────────────────────────────────
  {
    id: 'pincer-grasp',
    ageWeeksMin: 36,
    ageWeeksMax: 48,
    kind: 'wissenswertes',
    title: 'The pincer grasp is a precision milestone',
    title_de: 'Der Pinzettengriff ist ein Präzisions-Meilenstein',
    body: 'Around 9–10 months, your baby starts picking up small things with thumb and forefinger. This is a sign of brain wiring you cannot really speed up — but you can offer practice: peas, blueberries (halved), small pieces of soft pasta.',
    body_de: 'Mit etwa 9–10 Monaten beginnt dein Baby kleine Dinge zwischen Daumen und Zeigefinger zu greifen. Das ist eine Hirnverknüpfung, die du nicht beschleunigen kannst — aber Übung anbieten: Erbsen, halbierte Heidelbeeren, kleine weiche Nudelstücke.',
  },
  {
    id: 'choking-risks',
    ageWeeksMin: 36,
    ageWeeksMax: 200,
    kind: 'tipp',
    title: 'The choking-risk list to memorize',
    title_de: 'Die Verschluckungs-Risiko-Liste zum Auswendiglernen',
    body: 'Until age 4, avoid: whole grapes, whole nuts, popcorn, hot dog rounds, hard raw vegetables, marshmallows, big spoonfuls of nut butter. The pattern: round, firm, slippery, or sticky and dense.',
    body_de: 'Bis zum 4. Geburtstag meiden: ganze Weintrauben, ganze Nüsse, Popcorn, Würstchenscheiben, harte Rohkost, Marshmallows, dicke Klumpen Nussmus. Das Muster: rund, fest, rutschig, oder klebrig und dicht.',
  },
  {
    id: 'stranger-anxiety',
    ageWeeksMin: 28,
    ageWeeksMax: 52,
    kind: 'wissenswertes',
    title: 'Stranger anxiety is a sign of healthy attachment',
    title_de: 'Fremdeln ist ein Zeichen gesunder Bindung',
    body: 'Suddenly your 8-month-old screams when Grandma holds them. This is normal attachment in action — your baby has figured out who their "safe" people are. Peaks at 8–10 months, fades through year one.',
    body_de: 'Plötzlich schreit dein 8-Monats-Baby, wenn Oma es hält. Das ist normale Bindung in Aktion — dein Baby hat verstanden, wer seine „sicheren" Menschen sind. Höhepunkt mit 8–10 Monaten, lässt im ersten Jahr nach.',
  },
  {
    id: 'object-permanence',
    ageWeeksMin: 32,
    ageWeeksMax: 52,
    kind: 'wissenswertes',
    title: 'Why peekaboo suddenly becomes hilarious',
    title_de: 'Warum Kuckuck plötzlich lustig ist',
    body: 'At 8–10 months, your baby figures out objects exist even when hidden — object permanence. That is why peekaboo cracks them up. Same reason bedtime goodbyes get harder: they know you exist when you leave.',
    body_de: 'Mit 8–10 Monaten begreift dein Baby, dass Dinge existieren, auch wenn sie versteckt sind — Objektpermanenz. Deshalb wird Kuckuck-Spiel plötzlich hysterisch. Aus dem gleichen Grund werden Bettzeit-Verabschiedungen schwerer.',
  },
  {
    id: 'no-cow-milk-as-drink',
    ageWeeksMin: 36,
    ageWeeksMax: 52,
    kind: 'tipp',
    title: 'Cow milk as a drink: wait until 12 months',
    title_de: 'Kuhmilch als Hauptgetränk: bis 12 Monate warten',
    body: 'Cow milk as a primary drink before 1 can stress the kidneys and reduce iron absorption. But yogurt and cheese in food are fine — even encouraged. The line is "drink it in volume" vs "eat it as food".',
    body_de: 'Kuhmilch als Hauptgetränk vor dem 1. Geburtstag belastet die Nieren und reduziert die Eisenaufnahme. Aber Joghurt und Käse als Beikost sind okay — sogar erwünscht. Die Grenze ist „literweise trinken" vs. „als Mahlzeit essen".',
  },
  {
    id: 'crawl-styles',
    ageWeeksMin: 28,
    ageWeeksMax: 44,
    kind: 'wissenswertes',
    title: 'There are six different ways to crawl',
    title_de: 'Es gibt sechs verschiedene Arten zu krabbeln',
    body: 'Classic on hands and knees is just one. Army crawl, bear crawl, scoot, crab walk, bum-shuffle — all count as locomotion. Some babies skip crawling and go straight to pulling up. What pediatricians watch: are both sides used symmetrically.',
    body_de: 'Das klassische Krabbeln auf Händen und Knien ist nur eine Variante. Robben, Bären-Krabbeln, Rutschen, Krebsen, Po-Schieben — alles zählt als Fortbewegung. Manche Babys überspringen das Krabbeln. Was Kinderärztinnen prüfen: werden beide Körperseiten symmetrisch benutzt.',
  },

  // ──────────────────────────────────────────────────────────────
  // 12–18 months
  // ──────────────────────────────────────────────────────────────
  {
    id: 'appetite-drop-one',
    ageWeeksMin: 50,
    ageWeeksMax: 78,
    kind: 'wissenswertes',
    title: 'Appetite drops around the first birthday',
    title_de: 'Der Appetit fällt um den ersten Geburtstag',
    body: "Babies triple their birth weight in 12 months. They cannot keep that pace — growth slows, calorie needs drop. The 'food strike' at 12–14 months is biology, not personality. Keep offering without pressure.",
    body_de: 'Babys verdreifachen ihr Geburtsgewicht in 12 Monaten. Sie können dieses Tempo nicht halten — das Wachstum verlangsamt sich, der Kalorienbedarf sinkt. Der „Essstreik" mit 12–14 Monaten ist Biologie, nicht Charakter. Weiter anbieten, ohne Druck.',
  },
  {
    id: 'whole-milk-not-low-fat',
    ageWeeksMin: 52,
    ageWeeksMax: 104,
    kind: 'tipp',
    title: 'Whole milk, not low-fat, until age 2',
    title_de: 'Vollmilch, nicht fettarm, bis zum 2. Geburtstag',
    body: "Toddlers need the fat for brain development. After age 2, you can switch to 1.5% if growth is on track. Plant milks (almond, oat) are not recommended as the primary milk under 2 unless there's a specific reason.",
    body_de: 'Kleinkinder brauchen das Fett für die Hirnentwicklung. Nach dem 2. Geburtstag kann auf 1,5 % gewechselt werden, wenn das Wachstum okay ist. Pflanzenmilch (Mandel, Hafer) ist unter 2 Jahren nicht als Hauptmilch empfohlen, ausser es gibt einen medizinischen Grund.',
  },
  {
    id: 'reading-aloud',
    ageWeeksMin: 52,
    ageWeeksMax: 156,
    kind: 'wissenswertes',
    title: 'Reading aloud is the single highest-yield activity',
    title_de: 'Vorlesen ist die wirkungsvollste Einzelaktivität',
    body: "Research on early language is unusually clear: 15–20 minutes of shared reading a day predicts vocabulary, school readiness, and long-term outcomes. The mechanism is the back-and-forth, not the words on the page.",
    body_de: 'Die Forschung zur frühen Sprache ist hier ungewöhnlich klar: 15–20 Minuten gemeinsames Lesen täglich prognostiziert Wortschatz, Schulreife und langfristige Bildungsergebnisse. Der Mechanismus ist das Hin und Her, nicht die Worte auf der Seite.',
  },
  {
    id: 'screen-time-rules',
    ageWeeksMin: 52,
    ageWeeksMax: 104,
    kind: 'tipp',
    title: 'Screen rules: under 18 months, video calls only',
    title_de: 'Bildschirm-Regel: unter 18 Monaten nur Videoanrufe',
    body: 'AAP guidance: no screens except video calls until 18 months. Between 18–24 months, only high-quality content with a parent co-watching, narrating. Co-watching is the keyword — passive solo screen does nothing for language at this age.',
    body_de: 'AAP / DGKJ Empfehlung: keine Bildschirme ausser Videoanrufen bis 18 Monate. Zwischen 18–24 Monaten nur hochwertige Inhalte gemeinsam mit Eltern, mit Kommentieren. Co-Watching ist das Stichwort — passiv allein bringt nichts für die Sprachentwicklung.',
  },
  {
    id: 'first-words-range',
    ageWeeksMin: 52,
    ageWeeksMax: 78,
    kind: 'wissenswertes',
    title: 'First-word ranges are wide',
    title_de: 'Erste-Wort-Spannweite ist gross',
    body: 'Most toddlers have 1–3 clear words at 12 months and 10–20 by 18 months. But the range is wide — some children watch silently for months then explode at 18. What pediatricians watch is comprehension: do they understand "where is the ball?"',
    body_de: 'Die meisten Kleinkinder haben 1–3 klare Wörter mit 12 Monaten und 10–20 mit 18 Monaten. Aber die Spannweite ist gross — manche Kinder beobachten still und explodieren mit 18. Was Kinderärztinnen beobachten: das Verstehen — versteht das Kind „wo ist der Ball?"',
  },
  {
    id: 'first-tantrum',
    ageWeeksMin: 56,
    ageWeeksMax: 78,
    kind: 'wissenswertes',
    title: 'First tantrums show up around 14–18 months',
    title_de: 'Erste Trotzanfälle erscheinen mit 14–18 Monaten',
    body: 'Big emotions plus tiny language equals a meltdown. The emotion-regulation part of the brain is literally still building. Most tantrums get waited out, not solved. Stay calm, stay close, hold the boundary.',
    body_de: 'Grosse Gefühle plus winzige Sprache ergibt einen Trotzanfall. Die Hirnregion für Emotionsregulation ist buchstäblich noch im Aufbau. Die meisten Trotzanfälle werden ausgesessen, nicht gelöst. Ruhig bleiben, nah bleiben, Grenze halten.',
  },

  // ──────────────────────────────────────────────────────────────
  // 18–24 months
  // ──────────────────────────────────────────────────────────────
  {
    id: 'no-as-tool',
    ageWeeksMin: 74,
    ageWeeksMax: 110,
    kind: 'wissenswertes',
    title: '"No" becomes the favorite word',
    title_de: '„Nein" wird das Lieblingswort',
    body: 'At 17–20 months, "no" enters the vocabulary and gets applied to everything — including things they want. This is not pure opposition. "No" is an easy word and they are testing what it does. Try false choices: "red cup or blue cup?" beats "do you want milk?"',
    body_de: 'Mit 17–20 Monaten taucht „nein" auf und wird auf alles angewendet — auch auf Sachen, die sie wollen. Das ist nicht reine Opposition. „Nein" ist ein einfaches Wort und sie testen, was es bewirkt. Falsche Wahl hilft: „rote oder blaue Tasse?" schlägt „willst du Milch?"',
  },
  {
    id: 'parallel-play',
    ageWeeksMin: 78,
    ageWeeksMax: 130,
    kind: 'wissenswertes',
    title: 'Parallel play comes before sharing',
    title_de: 'Paralleles Spielen kommt vor Teilen',
    body: 'Toddlers around 18–24 months play next to other children, not with them. They observe, copy, occasionally swap toys. True cooperative play with sharing arrives around age 3. Do not force sharing at 2 — they cannot yet.',
    body_de: 'Kleinkinder um 18–24 Monate spielen neben anderen Kindern, nicht mit ihnen. Sie beobachten, kopieren, tauschen gelegentlich Spielzeug. Echtes kooperatives Spielen mit Teilen kommt erst um 3. Teilen mit 2 nicht erzwingen — sie können es noch nicht.',
  },
  {
    id: 'vocab-explosion',
    ageWeeksMin: 78,
    ageWeeksMax: 104,
    kind: 'wissenswertes',
    title: 'The vocabulary explosion is real',
    title_de: 'Die Wortschatz-Explosion ist real',
    body: "Between 18 and 24 months, many toddlers go from a handful of words to 50, 100, even 200 in weeks. Often accompanied by a personality shift — quieter or briefly more frustrated. Books, narrating your day, mirroring their attempts: all support it.",
    body_de: 'Zwischen 18 und 24 Monaten gehen viele Kleinkinder von einer Handvoll Wörter zu 50, 100, sogar 200 in wenigen Wochen. Oft begleitet von Verhaltensveränderung — ruhiger oder kurz frustrierter. Bücher, Tag mitsprechen, Versuche zurückspiegeln: alles hilft.',
  },
  {
    id: 'picky-eating-peak',
    ageWeeksMin: 78,
    ageWeeksMax: 130,
    kind: 'wissenswertes',
    title: 'Picky eating peaks just before 2',
    title_de: 'Mäkeliges Essen gipfelt kurz vor 2',
    body: "Yesterday's favorite gets rejected today. They eat 3 crackers and call it lunch. The Ellyn Satter approach — parents decide what/when/where; child decides if/how much — has the most evidence. Pressure backfires.",
    body_de: 'Gestern noch Lieblingsessen, heute verweigert. 3 Cracker werden zur Mittagsmahlzeit erklärt. Der Ellyn-Satter-Ansatz — Eltern entscheiden was/wann/wo; Kind entscheidet ob/wieviel — hat die meiste Evidenz. Druck geht meist nach hinten los.',
  },
  {
    id: 'climb-everything',
    ageWeeksMin: 60,
    ageWeeksMax: 130,
    kind: 'tipp',
    title: 'When walking is solid, climbing starts',
    title_de: 'Wenn das Laufen sitzt, beginnt das Klettern',
    body: 'Within weeks of independent walking, toddlers start climbing — sofas, stairs, kitchen chairs. Time to anchor furniture (IKEA Malm tipover kits exist for a reason), gate the stairs, move climbable furniture away from windows.',
    body_de: 'Innerhalb von Wochen nach den ersten freien Schritten beginnt das Klettern — Sofas, Treppen, Küchenstühle. Zeit Möbel zu ankern (IKEA Malm-Kippsicherungen gibt es nicht ohne Grund), Treppen sichern, kletterbare Möbel weg von Fenstern.',
  },
  {
    id: 'why-questions',
    ageWeeksMin: 95,
    ageWeeksMax: 156,
    kind: 'wissenswertes',
    title: 'The "why?" phase is conversation, not interrogation',
    title_de: 'Die „Warum?"-Phase ist Gespräch, kein Verhör',
    body: 'Around 2–3 years, "why?" arrives. The point is usually not the answer — it is extending the conversation, processing the world. "I don\'t know, what do you think?" is a perfectly valid response.',
    body_de: 'Zwischen 2 und 3 Jahren kommt „warum?". Der Punkt ist meist nicht die Antwort — es geht ums Gespräch verlängern, Welt verarbeiten. „Ich weiss es nicht, was glaubst du?" ist eine völlig okaye Antwort.',
  },
];

// ────────────────────────────────────────────────────────────────────
// Locale accessor
// ────────────────────────────────────────────────────────────────────

const KIND_LABEL: Record<TipKind, string> = {
  tipp: 'Tip of the day',
  wissenswertes: 'Worth knowing',
};

const KIND_LABEL_DE: Record<TipKind, string> = {
  tipp: 'Tipp des Tages',
  wissenswertes: 'Wissenswertes',
};

export function localeTip(tip: DailyTip): LocalizedTip {
  const de = isGerman();
  return {
    id: tip.id,
    kind: tip.kind,
    kindLabel: (de ? KIND_LABEL_DE : KIND_LABEL)[tip.kind],
    title: de ? tip.title_de : tip.title,
    body: de ? tip.body_de : tip.body,
  };
}

// ────────────────────────────────────────────────────────────────────
// Daily rotation — same seeding as recipeOfTheDayFor
// ────────────────────────────────────────────────────────────────────

export function tipOfTheDayFor(
  ageWeeks: number,
  babyId: string,
  date: Date = new Date()
): DailyTip | null {
  const pool = DAILY_TIPS.filter(
    (t) => ageWeeks >= t.ageWeeksMin && ageWeeks <= t.ageWeeksMax
  );
  if (pool.length === 0) return null;

  // Day-of-year + baby id hash. Offset by 7 from recipe seed so tip and
  // recipe rotate independently (same baby same day shouldn't cycle in
  // lockstep).
  const start = Date.UTC(date.getUTCFullYear(), 0, 0);
  const dayOfYear = Math.floor((date.getTime() - start) / 86400000);

  let hash = (date.getUTCFullYear() * 366 + dayOfYear + 7) >>> 0;
  for (let i = 0; i < babyId.length; i++) {
    hash = (Math.imul(hash, 31) + babyId.charCodeAt(i)) >>> 0;
  }

  return pool[hash % pool.length];
}
